// S9 "Neues Projekt" modal. Fields per UX-spec: Name (required), Beschreibung,
// Owner (default = current user), Color, Start-/Zieldatum.

import { useState, useEffect } from "preact/hooks";
import { useMe } from "../lib/auth";
import { useUsers } from "../store/users";
import {
  ProjectCreateSchema,
  type ProjectCreate,
  type ParseProjectPlanResult,
  type ProjectTemplateItem,
  type ProjectTemplatePayload,
  type ProjectVisibility,
} from "@juuplan/shared";
import { Building2, FileUp, Lock, Sparkles, Users } from "lucide-preact";
import { createProject, createProjectFromStructure } from "../store/projects";
import { aiParseProjectPlan, aiSuggestProjectStructure, ApiError } from "../lib/api";
import { useTemplates, createProjectFromTemplate } from "../store/templates";
import { useDepartments } from "../store/departments";
import { useUserPrefs } from "../store/userPrefs";
import { COLOR_PRESETS } from "../lib/format";
import { toastSuccess } from "../lib/toast";
import { Modal } from "./Modal";
import { useSpaces } from "../store/spaces";
import { useFolders } from "../store/folders";

import { Button } from "./ui/Button";

interface Props {
  onClose: () => void;
  onCreated: (id: number) => void;
  /** Pre-select a space when opening the modal from a sidebar space affordance. */
  defaultSpaceId?: number;
}

type Mode = "blank" | "template" | "plan";

// Mirrors the server-side ParseProjectPlanSchema cap (50k chars). The whole file
// is read into memory and egressed, so we reject oversized uploads client-side.
const MAX_PLAN_CHARS = 50_000;

// ProjectCreateSchema.description cap — used by the plan fallback below.
const MAX_DESCRIPTION_CHARS = 10_000;

/**
 * Derive a project name from a raw Markdown plan when the AI produced none:
 * first Markdown heading (# … ######), else the first non-empty line, else a
 * generic label. Trimmed to the 200-char name cap. Keeps the "Aus Plan" flow
 * from ever dead-ending without a name.
 */
function deriveNameFromPlan(plan: string): string {
  const lines = plan.split(/\r?\n/);
  const heading = lines
    .map((l) => l.match(/^#{1,6}\s+(.+?)\s*$/)?.[1])
    .find((h): h is string => Boolean(h));
  const firstLine = lines.map((l) => l.trim()).find(Boolean);
  return (heading ?? firstLine ?? "Importiertes Projekt").slice(0, 200);
}

/**
 * Today as a local ISO date (YYYY-MM-DD). The AI structure flows derive per-task
 * day-offsets from the project start; without a start date those offsets never
 * materialise, so these flows default the start to today unless the user set one.
 */
function todayIso(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
}

export function NewProjectModal(props: Props): preact.JSX.Element {
  const { onClose, onCreated, defaultSpaceId } = props;
  const me = useMe();
  const users = useUsers();
  const templates = useTemplates();
  const spaces = useSpaces();

  // Resolve the initial space: prefer defaultSpaceId prop, else the general
  // space (slug === "general"), else the first space once loaded.
  const resolveInitialSpaceId = (): number | undefined => {
    if (defaultSpaceId !== undefined) return defaultSpaceId;
    if (spaces.items.length === 0) return undefined;
    const general = spaces.items.find((s) => s.slug === "general");
    return general?.id ?? spaces.items[0]?.id;
  };

  const [mode, setMode] = useState<Mode>("blank");
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [ownerId, setOwnerId] = useState<number | "">("");
  // Default 'shared': jedes neue Projekt landet in einem Space (siehe
  // resolveInitialSpaceId unten), und Space-Sichtbarkeit ist der gewollte
  // Standard-Scope — NICHT organisationsweit. 'org' bleibt eine bewusste
  // Auswahl des Users.
  const [visibility, setVisibility] = useState<ProjectVisibility>("shared");
  const [color, setColor] = useState<string>(COLOR_PRESETS[0]);
  const [startDate, setStartDate] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiStructure, setAiStructure] = useState<ProjectTemplatePayload | null>(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [ai503, setAi503] = useState(false);
  // "Aus Plan" mode: the uploaded Markdown plan + its file name + inline error.
  const [planText, setPlanText] = useState("");
  const [planFileName, setPlanFileName] = useState<string | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  // Sprint 3 Story 3 — Departments-Picker. Default: alle vom User abonnierten
  // (= seine sichtbaren) Abteilungen vorausgewählt damit das neue Projekt
  // direkt in seiner Sidebar erscheint.
  const departments = useDepartments();
  const userPrefs = useUserPrefs();
  const initialDepts =
    userPrefs.visible_departments.length > 0
      ? userPrefs.visible_departments
      : departments.items.length > 0
        ? [departments.items[0]!.id]
        : [];
  const [departmentIds, setDepartmentIds] = useState<number[]>(initialDepts);

  // Space + Folder picker state.
  const [selectedSpaceId, setSelectedSpaceId] = useState<number | undefined>(
    resolveInitialSpaceId
  );
  const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>(
    undefined
  );
  // Once spaces finish loading, auto-select the general space if nothing is
  // selected yet (handles the case where spaces were still loading when the
  // modal first rendered).
  useEffect(() => {
    if (selectedSpaceId === undefined && !spaces.loading && spaces.items.length > 0) {
      const general = spaces.items.find((s) => s.slug === "general");
      setSelectedSpaceId(general?.id ?? spaces.items[0]!.id);
    }
  }, [spaces.loading, spaces.items.length, selectedSpaceId]);

  // Load folders for the currently selected space (useFolders is called
  // unconditionally per hooks rules; pass 0 as a sentinel when no space is
  // selected — the store tolerates it and returns an empty entry).
  const folders = useFolders(selectedSpaceId ?? 0);

  const effectiveOwner: number | undefined =
    typeof ownerId === "number" ? ownerId : me.user?.id;

  const handleSuggestStructure = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setAiBusy(true);
    setAi503(false);
    try {
      const res = await aiSuggestProjectStructure(trimmed, description.trim() || undefined);
      setAiStructure(res.payload);
    } catch (err) {
      // api.ts hat den Fehler bereits getoastet; 503 bekommt zusätzlich einen
      // eigenen Inline-Hinweis (KI-Gateway down), andere Fehler nicht.
      if (err instanceof ApiError && err.status === 503) setAi503(true);
    } finally {
      setAiBusy(false);
    }
  };

  const handlePlanFile = async (e: Event): Promise<void> => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    setPlanError(null);
    if (!file) {
      setPlanText("");
      setPlanFileName(null);
      return;
    }
    let text: string;
    try {
      text = await file.text();
    } catch {
      setPlanText("");
      setPlanFileName(null);
      setPlanError("Datei konnte nicht gelesen werden.");
      return;
    }
    if (!text.trim()) {
      setPlanText("");
      setPlanFileName(null);
      setPlanError("Die Datei ist leer.");
      return;
    }
    if (text.length > MAX_PLAN_CHARS) {
      setPlanText("");
      setPlanFileName(null);
      setPlanError(
        `Datei zu groß (max. ${MAX_PLAN_CHARS.toLocaleString("de-DE")} Zeichen).`
      );
      return;
    }
    setPlanText(text);
    setPlanFileName(file.name);
  };

  const removeAiPhase = (pi: number): void =>
    setAiStructure((s) => s ? { phases: s.phases.filter((_, i) => i !== pi) } : s);

  const removeAiTask = (pi: number, ti: number): void =>
    setAiStructure((s) =>
      s
        ? {
            phases: s.phases.map((ph, i) =>
              i === pi ? { ...ph, tasks: ph.tasks.filter((_, j) => j !== ti) } : ph
            ),
          }
        : s
    );

  const onSubmit = async (e: Event): Promise<void> => {
    e.preventDefault();
    setError(null);

    const trimmedName = name.trim();

    // "Aus Plan" — the AI parses the uploaded Markdown into name/description +
    // structure, then we create the project from that structure in one shot.
    // The name field is optional here (the AI derives it); a typed name wins.
    if (mode === "plan") {
      if (!planText.trim()) {
        setError("Bitte eine Markdown-Datei mit dem Projektplan hochladen.");
        return;
      }
      setSubmitting(true);
      try {
        // Fields shared by the structured and the fallback path. Default the
        // start to today so the AI-derived task offsets land on the timeline.
        const basePayload = {
          visibility,
          color,
          owner_id: effectiveOwner,
          start_date: startDate || todayIso(),
          target_date: targetDate || null,
          department_ids: departmentIds.length > 0 ? departmentIds : undefined,
          space_id: selectedSpaceId,
          folder_id: selectedFolderId ?? null,
        };

        // 1) Try the AI structure. ANY failure — AI disabled, gateway/timeout
        //    error, or an empty result — degrades to a plain project below, so
        //    a plan upload ALWAYS yields a project instead of a dead-end error.
        let structure: ParseProjectPlanResult | null = null;
        try {
          const parsed = await aiParseProjectPlan(planText);
          if (parsed.payload.phases.length > 0) structure = parsed;
        } catch {
          // swallowed on purpose — the fallback path takes over
        }

        if (structure) {
          const planPayload: ProjectCreate = {
            ...basePayload,
            name: trimmedName || structure.name,
            description: structure.description,
          };
          const parsedProject = ProjectCreateSchema.safeParse(planPayload);
          if (parsedProject.success) {
            const created = await createProjectFromStructure({
              ...parsedProject.data,
              payload: structure.payload,
            });
            toastSuccess(`Projekt „${created.name}" aus Plan angelegt.`);
            onCreated(created.id);
            return;
          }
          // invalid structured payload → fall through to the plain fallback
        }

        // 2) Fallback: create a plain project from the raw Markdown and keep the
        //    plan text as the description so nothing the user uploaded is lost.
        const fallbackPayload: ProjectCreate = {
          ...basePayload,
          name: trimmedName || deriveNameFromPlan(planText),
          description: planText.trim().slice(0, MAX_DESCRIPTION_CHARS),
        };
        const parsedFallback = ProjectCreateSchema.safeParse(fallbackPayload);
        if (!parsedFallback.success) {
          setError(parsedFallback.error.issues[0]?.message ?? "Eingabe ungültig");
          return;
        }
        const created = await createProject(parsedFallback.data);
        toastSuccess(
          `Projekt „${created.name}" angelegt. Die KI konnte keine Phasen ableiten — der Plan wurde als Beschreibung gespeichert.`
        );
        onCreated(created.id);
      } catch (err) {
        // Only reached if the actual project-create call fails (api() already
        // surfaced a toast for it). Keep an inline hint with the real reason.
        setError(
          err instanceof ApiError && err.message
            ? err.message
            : "Der Plan konnte nicht verarbeitet werden. Bitte erneut versuchen."
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (!trimmedName) {
      setError("Bitte einen Projektnamen eingeben.");
      return;
    }

    if (mode === "template") {
      if (templateId === null) {
        setError("Bitte ein Template auswählen.");
        return;
      }
      setSubmitting(true);
      try {
        const created = await createProjectFromTemplate({
          template_id: templateId,
          name: trimmedName,
          owner_id: effectiveOwner,
          start_date: startDate || undefined,
        });
        toastSuccess(`Projekt „${created.name}" aus Template angelegt.`);
        onCreated(created.id);
      } catch {
        // toast already surfaced via api()
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // When an AI structure is attached its tasks carry day-offsets, so default
    // the start to today (unless set) so they land on the timeline. A plain
    // blank project keeps a null start.
    const hasAiStructure = aiStructure !== null && aiStructure.phases.length > 0;
    const payload: ProjectCreate = {
      name: trimmedName,
      description: description.trim() || null,
      visibility,
      color,
      owner_id: effectiveOwner,
      start_date: startDate || (hasAiStructure ? todayIso() : null),
      target_date: targetDate || null,
      department_ids: departmentIds.length > 0 ? departmentIds : undefined,
      space_id: selectedSpaceId,
      folder_id: selectedFolderId ?? null,
    };

    const parsed = ProjectCreateSchema.safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Eingabe ungültig");
      return;
    }

    setSubmitting(true);
    try {
      const created =
        hasAiStructure && aiStructure
          ? await createProjectFromStructure({ ...parsed.data, payload: aiStructure })
          : await createProject(parsed.data);
      toastSuccess(`Projekt „${created.name}" angelegt.`);
      onCreated(created.id);
    } catch {
      // api.ts already surfaces a toast for the server error; we just need to
      // re-enable the submit button so the user can correct & retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Neues Projekt"
      onClose={onClose}
      size="md"
      dirty={
        name.trim() !== "" ||
        description.trim() !== "" ||
        planText.trim() !== "" ||
        startDate !== "" ||
        targetDate !== ""
      }
    >
      <form onSubmit={onSubmit} class="flex flex-col gap-4">
        {/* Mode toggle — "Leer" (default) | "Aus Template" */}
        <div
          role="radiogroup"
          aria-label="Wie soll das Projekt angelegt werden?"
          class="inline-flex p-1 gap-1 rounded-md bg-[color:var(--color-surface-container-low)] border border-[color:var(--color-outline-variant)] self-start"
        >
          <ModeButton
            label="Leer"
            active={mode === "blank"}
            onClick={() => setMode("blank")}
          />
          <ModeButton
            label="Aus Template"
            active={mode === "template"}
            onClick={() => setMode("template")}
          />
          {me.user?.ai_enabled === true && (
            <ModeButton
              label="Aus Plan"
              active={mode === "plan"}
              onClick={() => setMode("plan")}
            />
          )}
        </div>

        <Field label="Name" htmlFor="np-name" required={mode !== "plan"}>
          <input
            id="np-name"
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            required={mode !== "plan"}
            maxLength={200}
            autoFocus
            class={INPUT_CLS}
            placeholder={
              mode === "plan" ? "Optional — wird sonst aus dem Plan abgeleitet" : "z.B. juuplan v1"
            }
          />
        </Field>

        {mode === "plan" && (
          <Field label="Projektplan (Markdown)" htmlFor="np-plan">
            <input
              id="np-plan"
              type="file"
              accept=".md,.markdown,text/markdown,text/plain"
              onChange={(e) => void handlePlanFile(e)}
              class="block w-full text-sm text-[color:var(--color-on-surface-variant)] file:mr-3 file:rounded file:border-0 file:bg-[color:var(--color-primary)] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-[color:var(--color-on-primary)] hover:file:bg-[color:var(--color-primary-strong)] file:cursor-pointer"
            />
            {planFileName && (
              <p class="mt-1.5 text-xs text-[color:var(--color-on-surface)]">
                <span class="font-medium">{planFileName}</span> geladen — die KI
                erzeugt Name, Beschreibung, Phasen, Aufgaben und Subtasks.
              </p>
            )}
            {planError && (
              <p class="mt-1.5 text-xs text-[color:var(--color-error)]" role="alert">
                {planError}
              </p>
            )}
            <p class="mt-1.5 text-label text-[color:var(--color-on-surface-variant)] inline-flex items-center gap-1">
              <FileUp size={14} aria-hidden="true" />
              Lade eine .md-Datei mit dem Projektplan hoch. Die KI legt das
              Projekt anhand des Plans selbständig an.
            </p>
          </Field>
        )}

        {mode === "blank" && (
          <Field label="Beschreibung" htmlFor="np-desc">
            <textarea
              id="np-desc"
              value={description}
              onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
              rows={3}
              maxLength={10000}
              class={INPUT_CLS}
              placeholder="Optionale Beschreibung (Markdown)"
            />
          </Field>
        )}

        {mode === "blank" && me.user?.ai_enabled === true && (
          <div class="flex flex-col gap-2">
            <button
              type="button"
              onClick={handleSuggestStructure}
              disabled={aiBusy || !name.trim()}
              class="inline-flex items-center gap-1.5 self-start rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors disabled:opacity-50 min-h-[32px]"
            >
              <Sparkles size={14} aria-hidden="true" />
              {aiBusy ? "Schlägt vor…" : "Struktur vorschlagen"}
            </button>

            {ai503 && (
              <p class="text-xs italic text-[color:var(--color-on-surface-variant)]">
                KI ist derzeit deaktiviert.
              </p>
            )}

            {aiStructure && aiStructure.phases.length > 0 && (
              <div class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] p-3 flex flex-col gap-2">
                <p class="text-label text-[color:var(--color-on-surface-variant)] italic">
                  Diese Struktur wird beim Anlegen erzeugt. Du kannst Punkte entfernen.
                </p>
                <ul class="flex flex-col gap-1.5">
                  {aiStructure.phases.map((phase, pi) => (
                    <li key={pi} class="flex flex-col gap-1">
                      <div class="flex items-center justify-between gap-2">
                        <span class="text-xs font-semibold text-[color:var(--color-on-surface)] truncate">
                          {phase.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeAiPhase(pi)}
                          aria-label={`Phase „${phase.name}" entfernen`}
                          class="shrink-0 rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-highest)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
                        >
                          ✕
                        </button>
                      </div>
                      {phase.tasks.length > 0 && (
                        <ul class="pl-3 flex flex-col gap-0.5">
                          {phase.tasks.map((task, ti) => (
                            <li key={ti} class="flex items-center justify-between gap-2">
                              <span class="text-xs text-[color:var(--color-on-surface-variant)] truncate">
                                {task.title}
                                {task.is_milestone && " ◆"}
                                {task.subtasks && task.subtasks.length > 0 && (
                                  <span class="text-label text-[color:var(--color-on-surface-variant)] opacity-70">
                                    {" "}+{task.subtasks.length} Unteraufgaben
                                  </span>
                                )}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeAiTask(pi, ti)}
                                aria-label={`Aufgabe „${task.title}" entfernen`}
                                class="shrink-0 rounded p-0.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-highest)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors text-label"
                              >
                                ✕
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {aiStructure && aiStructure.phases.length === 0 && (
              <p class="text-xs italic text-[color:var(--color-on-surface-variant)]">
                Kein Strukturvorschlag erhalten.
              </p>
            )}
          </div>
        )}

        {mode === "blank" && (
          <Field label="Sichtbarkeit" htmlFor="np-visibility">
            <div
              id="np-visibility"
              class="inline-flex w-full rounded border border-[color:var(--color-outline-variant)] overflow-hidden"
              role="group"
              aria-label="Projekt-Sichtbarkeit"
            >
              {(
                [
                  { value: "private", label: "Privat", icon: <Lock size={14} aria-hidden="true" /> },
                  { value: "shared", label: "Geteilt", icon: <Users size={14} aria-hidden="true" /> },
                  { value: "org", label: "Organisation", icon: <Building2 size={14} aria-hidden="true" /> },
                ] as { value: ProjectVisibility; label: string; icon: preact.JSX.Element }[]
              ).map((opt) => {
                const active = visibility === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setVisibility(opt.value)}
                    aria-pressed={active}
                    class={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs font-medium transition-colors min-h-[32px] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--color-primary)] ${
                      active
                        ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)]"
                        : "bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                    }`}
                    title={opt.label}
                  >
                    {opt.icon}
                    <span class="hidden sm:inline">{opt.label}</span>
                  </button>
                );
              })}
            </div>
            {visibility === "private" && (
              <p class="mt-1.5 text-label text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)] rounded px-2 py-1.5">
                <strong>Privat:</strong> Nur du und später explizit freigegebene
                Personen sehen dieses Projekt.
              </p>
            )}
          </Field>
        )}

        {/* Sprint 3 Story 3: Department-Picker. Ohne Auswahl → Gesamtorganisation. */}
        <Field label="Abteilungen" htmlFor="np-departments">
          <div
            id="np-departments"
            class="flex flex-wrap gap-1.5"
          >
            {departments.loading ? (
              <span class="text-xs text-[color:var(--color-on-surface-variant)]">
                Lädt…
              </span>
            ) : (
              departments.items.map((d) => {
                const checked = departmentIds.includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => {
                      setDepartmentIds((prev) =>
                        prev.includes(d.id)
                          ? prev.filter((x) => x !== d.id)
                          : [...prev, d.id]
                      );
                    }}
                    aria-pressed={checked}
                    class={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium border transition-colors ${
                      checked
                        ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)] border-transparent"
                        : "border-[color:var(--color-outline-variant)] text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                    }`}
                  >
                    {d.name}
                  </button>
                );
              })
            )}
          </div>
          <p class="text-label text-[color:var(--color-on-surface-variant)] mt-1">
            Ohne Auswahl wird die Gesamtorganisation gesetzt.
          </p>
        </Field>

        {/* Space + Folder picker — hidden for external users who cannot choose a space. */}
        {!me.user?.is_external && (
          <Field label="Space" htmlFor="np-space">
            <select
              id="np-space"
              value={selectedSpaceId !== undefined ? String(selectedSpaceId) : ""}
              onChange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                setSelectedSpaceId(v ? Number(v) : undefined);
                setSelectedFolderId(undefined);
              }}
              class={INPUT_CLS}
            >
              {spaces.loading && spaces.items.length === 0 ? (
                <option value="">Lädt…</option>
              ) : (
                spaces.items.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))
              )}
            </select>
          </Field>
        )}

        {!me.user?.is_external && folders.items.length > 0 && (
          <Field label="Ordner" htmlFor="np-folder">
            <select
              id="np-folder"
              value={selectedFolderId !== undefined ? String(selectedFolderId) : ""}
              onChange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                setSelectedFolderId(v ? Number(v) : undefined);
              }}
              class={INPUT_CLS}
            >
              <option value="">(kein Ordner)</option>
              {folders.items.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </Field>
        )}

        {mode === "template" && (
          <Field label="Template" htmlFor="np-template">
            <TemplatePickerList
              items={templates.items}
              loading={templates.loading}
              error={templates.error}
              selectedId={templateId}
              onSelect={setTemplateId}
            />
          </Field>
        )}

        <Field label="Owner" htmlFor="np-owner">
          <select
            id="np-owner"
            value={ownerId === "" ? "" : String(ownerId)}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              setOwnerId(v ? Number(v) : "");
            }}
            class={INPUT_CLS}
          >
            <option value="">
              {me.user ? `Ich (${me.user.name})` : "Aktueller Nutzer"}
            </option>
            {users.items.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} — {u.email}
              </option>
            ))}
          </select>
        </Field>

        {mode === "blank" && (
          <Field label="Farbe" htmlFor="np-color">
            <div id="np-color" class="flex items-center gap-2 flex-wrap">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  class={`size-8 rounded border-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition ${
                    color === c
                      ? "border-[color:var(--color-on-surface)]"
                      : "border-transparent"
                  }`}
                  style={{ background: c }}
                  aria-label={`Farbe ${c}`}
                  aria-pressed={color === c}
                />
              ))}
            </div>
          </Field>
        )}

        <div class="grid grid-cols-2 gap-3">
          <Field label="Startdatum" htmlFor="np-start">
            <input
              id="np-start"
              type="date"
              value={startDate}
              onInput={(e) => setStartDate((e.target as HTMLInputElement).value)}
              class={INPUT_CLS}
            />
          </Field>
          {mode === "blank" && (
            <Field label="Zieldatum" htmlFor="np-target">
              <input
                id="np-target"
                type="date"
                value={targetDate}
                min={startDate || undefined}
                onInput={(e) => setTargetDate((e.target as HTMLInputElement).value)}
                class={INPUT_CLS}
              />
            </Field>
          )}
        </div>

        {error && (
          <p
            role="alert"
            tabIndex={-1}
            // Scroll + focus on mount: the alert sits at the bottom of a long
            // scrolling form, so a user who is scrolled up would never see it.
            ref={(el) => {
              el?.scrollIntoView({ block: "nearest" });
              el?.focus();
            }}
            class="rounded border border-[color:var(--color-error-container)] bg-[color:var(--color-error-container)] px-3 py-2 text-sm text-[color:var(--color-on-error-container)] focus:outline-none"
          >
            {error}
          </p>
        )}

        <div class="flex items-center justify-end gap-2 pt-2 border-t border-[color:var(--color-outline-variant)] -mx-5 -mb-4 px-5 pb-4 mt-2">
          <button
            type="button"
            onClick={onClose}
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-4 py-2 text-sm font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
          >
            Abbrechen
          </button>
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={submitting || (mode === "plan" && !planText.trim())}
          >
            {submitting
              ? mode === "plan"
                ? "KI analysiert Plan… (bis zu 2 Min.)"
                : "Lege an…"
              : mode === "plan"
                ? "Plan analysieren & anlegen"
                : "Anlegen"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- helpers ----------

export const INPUT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--color-surface-container-lowest)] transition-shadow min-h-[32px]";

interface FieldProps {
  label: string;
  /**
   * Id of the labelled form control. Weggelassen, wenn das Feld KEIN einzelnes
   * Control enthält (z. B. der AssigneePicker mit Button + Portal-Menü) — dann
   * rendert das Feld ein <div> statt eines <label>. Ein <label> um einen Button
   * wäre sonst doppelt klickbar und als Verschachtelung ungültig.
   */
  htmlFor?: string;
  required?: boolean;
  children: preact.ComponentChildren;
}

export function Field(props: FieldProps): preact.JSX.Element {
  const caption = (
    <span class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
      {props.label}
      {props.required && <span class="text-[color:var(--color-error)] ml-0.5">*</span>}
    </span>
  );
  if (props.htmlFor === undefined) {
    return (
      <div class="flex flex-col gap-1">
        {caption}
        {props.children}
      </div>
    );
  }
  return (
    <label htmlFor={props.htmlFor} class="flex flex-col gap-1">
      {caption}
      {props.children}
    </label>
  );
}

// ---------- Mode toggle button ----------

function ModeButton(props: {
  label: string;
  active: boolean;
  onClick: () => void;
}): preact.JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={props.active}
      onClick={props.onClick}
      class={`px-3 py-1.5 text-xs font-medium rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 min-h-[28px] ${
        props.active
          ? "bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface)] shadow-sm"
          : "text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-on-surface)]"
      }`}
    >
      {props.label}
    </button>
  );
}

// ---------- Template picker list ----------

function TemplatePickerList(props: {
  items: ProjectTemplateItem[];
  loading: boolean;
  error: string | null;
  selectedId: number | null;
  onSelect: (id: number) => void;
}): preact.JSX.Element {
  const { items, loading, error, selectedId, onSelect } = props;

  if (loading && items.length === 0) {
    return (
      <p class="text-xs text-[color:var(--color-on-surface-variant)] px-1">
        Lade Templates…
      </p>
    );
  }

  if (error) {
    return (
      <p
        role="alert"
        class="text-xs text-[color:var(--color-error)] px-1"
      >
        Fehler beim Laden der Templates: {error}
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <p class="text-xs text-[color:var(--color-on-surface-variant)] px-1">
        Noch keine Templates vorhanden.
      </p>
    );
  }

  return (
    <ul
      id="np-template"
      role="listbox"
      aria-label="Template auswählen"
      class="flex flex-col gap-1.5 max-h-56 overflow-auto rounded border border-[color:var(--color-outline-variant)] p-1"
    >
      {items.map((t) => {
        const phaseCount = t.payload?.phases?.length ?? 0;
        const taskCount = (t.payload?.phases ?? []).reduce(
          (sum, p) => sum + (Array.isArray(p.tasks) ? p.tasks.length : 0),
          0
        );
        const isSelected = selectedId === t.id;
        return (
          <li key={t.id}>
            <button
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => onSelect(t.id)}
              class={`w-full text-left rounded px-3 py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
                isSelected
                  ? "bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]"
                  : "hover:bg-[color:var(--color-surface-container-low)] text-[color:var(--color-on-surface)]"
              }`}
            >
              <div class="text-sm font-medium truncate">{t.name}</div>
              {t.description && (
                <div class="text-xs text-[color:var(--color-on-surface-variant)] truncate mt-0.5">
                  {t.description}
                </div>
              )}
              <div class="text-label text-[color:var(--color-on-surface-variant)] mt-1">
                {phaseCount} Phasen · {taskCount} Aufgaben
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
